import { useState } from "react";

// Teacher reference for the composition scores. Condensed, criterion-referenced (describes what the writing
// DOES at each 0-4 level) plus a plain-language definition of each element. Source of truth:
// backend/writing_structure_analyzer.py `_SYS`. Reference only — changes nothing.
const DIMS = [
  {
    key: "orientation", label: "Orientation",
    def: "How effectively the writing establishes and directs the reader toward the work of the paragraph.",
    levels: [
      "Required orientation is absent.",
      "Territory identified — names the topic or situation, with little functional orientation.",
      "Context established — enough context to proceed.",
      "Orients toward the organizing work — points the reader at the question, problem, contrast, or realization the paragraph develops.",
      "Integrated — the opening itself is later developed, transformed, answered, or resolved.",
    ],
    note: "A competent definitional opener (“What is X? X is…”) is Context (level 2) and may be fully sufficient; mere later reference to the opening is not enough for a 4.",
  },
  {
    key: "organizing_structure", label: "Organizing Structure",
    def: "How the paragraph’s central meanings are organized.",
    levels: [
      "Absent.",
      "Topic is identifiable, but the material is mainly additive; no governing relation.",
      "A genuine organizing meaning is present but broad, generic, or undifferentiated.",
      "The organizing meaning is differentiated enough to coordinate the major material.",
      "Integrates distinctions and relations across the paragraph into a functioning whole.",
    ],
    note: "Having a thesis is not automatically a 3 — ask whether the meaning differentiates enough of the work to coordinate it. Organizing Structure and Relational Coherence are independent and need not match.",
  },
  {
    key: "elaboration", label: "Elaboration",
    def: "How meanings are developed and differentiated.",
    levels: [
      "Absent.",
      "Additive — ideas listed side by side (A + B + C).",
      "One or more local relationships developed, but uneven or local.",
      "Multiple important relationships developed and coordinated under the organizing meaning.",
      "Integrative or transformational — later meanings deepen, transform, or qualify earlier ones.",
    ],
    note: "3 = several relations working together; 4 = relations among relations / mutual development. No literal causal chain is required (narrative, reflective, and comparative forms qualify).",
  },
  {
    key: "grounding", label: "Grounding",
    def: "How ideas are grounded when grounding is required or useful for the particular claim or purpose. Grounding is conditional — not every paragraph requires evidence or examples.",
    levels: [
      "Required grounding is absent — an important meaning needs grounding and its absence blocks the work.",
      "Weak or mismatched — grounding is present but does not perform the required function.",
      "Appropriate — the kind and amount the claim needs (may be fully sufficient).",
      "The grounding-to-meaning relationship is itself developed.",
      "Grounding and meaning are integrated — they mutually develop or qualify each other.",
    ],
    notReq: "No meaningful grounding requirement exists anywhere. Excluded from the average; it is not a zero.",
    note: "Necessity is authoritative: absence is a deficit only when grounding was REQUIRED, never when it is merely useful. “Could be improved with an example” is not “an example is missing.”",
  },
  {
    key: "closure", label: "Closure",
    def: "How the paragraph brings its meanings to an appropriate conclusion.",
    levels: [
      "Required closure is absent — the paragraph stops mid-work.",
      "Ending or repetition — marks completion without resolving the developed work.",
      "Substantive resolution — completes the immediate work (more than merely stopping).",
      "Implication, synthesis, or significance — develops what follows or why it matters.",
      "Integrative resolution — draws together the relationships developed across the paragraph.",
    ],
    note: "A discrete concluding sentence is not required; embedded resolution can count. An opening↔conclusion return is strong evidence of a 4.",
  },
  {
    key: "relational_coherence", label: "Relational Coherence",
    def: "How effectively meanings and parts of the paragraph are related to one another.",
    levels: [
      "Fragmented.",
      "Additive or juxtaposed.",
      "Meaningful local relations.",
      "Coordinated — multiple functional relationships work together under the larger organization.",
      "Integrated — relations among relations; the relationships form an organized system.",
    ],
    note: "This is about relations among the functions of the writing (not integrative thinking about concepts — that is the separate Thinking Structure report). A 4 needs more than “very coherent.”",
  },
];
const grounding = DIMS.find((d) => d.key === "grounding");

export function CompositionCriteria() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);

  return (
    <>
      <button data-testid="composition-criteria-open" onClick={() => setOpen(true)}
        className="mt-5 ml-4 text-[13px] font-medium text-[#8C3A2A] hover:underline">
        Composition criteria — what do these scores mean?
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-stone-900/40 p-4 overflow-y-auto"
          data-testid="composition-criteria-modal" onClick={() => setOpen(false)}>
          <div className="bg-stone-50 rounded-lg shadow-xl max-w-5xl w-full my-8 border border-stone-200" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between px-6 pt-5 pb-3 border-b border-stone-200">
              <div>
                <h2 className="font-serif text-2xl text-stone-900">Composition criteria</h2>
                <p className="mt-1 text-[13px] text-stone-500 max-w-2xl">Scores describe what the writing does, 0–4. They are structural accomplishments, not quality grades — a lower score can still fully satisfy a prompt.</p>
              </div>
              <button data-testid="composition-criteria-close" onClick={() => setOpen(false)} className="text-stone-400 hover:text-stone-800 text-2xl leading-none">×</button>
            </div>

            <div className="p-6">
              <h3 className="text-[11px] font-mono uppercase tracking-wider text-stone-400 mb-2">What each element means</h3>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mb-6" data-testid="criteria-definitions">
                {DIMS.map((d) => (
                  <p key={d.key} className="text-[13px] text-stone-600" data-testid={`criteria-def-${d.key}`}>
                    <span className="font-medium text-stone-800">{d.label}:</span> {d.def}
                  </p>
                ))}
              </div>

              <h3 className="text-[11px] font-mono uppercase tracking-wider text-stone-400 mb-2">What earns each score</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-[12.5px] border-collapse">
                  <thead>
                    <tr className="border-b border-stone-200">
                      <th className="text-left px-2 py-2 w-14 text-[11px] font-mono uppercase tracking-wider text-stone-400">Score</th>
                      {DIMS.map((d) => (
                        <th key={d.key} className="text-left px-2 py-2 text-[12px] font-semibold text-stone-800 align-bottom cursor-pointer hover:text-[#8C3A2A] min-w-[150px]"
                          data-testid={`criteria-col-${d.key}`} onClick={() => setExpanded(expanded === d.key ? null : d.key)}>
                          {d.label}
                          <span className="block text-[10px] font-normal text-[#8C3A2A] mt-0.5">{expanded === d.key ? "hide detail" : "detail"}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[4, 3, 2, 1, 0].map((score) => (
                      <tr key={score} data-testid={`criteria-score-${score}`} className="border-b border-stone-100 align-top">
                        <td className="px-2 py-3 font-semibold text-[#8C3A2A] text-[14px]">{score}</td>
                        {DIMS.map((d) => (
                          <td key={d.key} className="px-2 py-3 text-stone-600 leading-snug">{d.levels[score]}</td>
                        ))}
                      </tr>
                    ))}
                    {expanded && (
                      <tr data-testid={`criteria-detail-${expanded}`} className="bg-white">
                        <td className="px-2 py-3 text-[11px] font-mono uppercase tracking-wider text-stone-400">Detail</td>
                        <td colSpan={DIMS.length} className="px-2 py-3 text-[13px] text-stone-600">
                          <span className="font-medium text-stone-800">{DIMS.find((x) => x.key === expanded)?.label}:</span> {DIMS.find((x) => x.key === expanded)?.note}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <p className="mt-3 text-[12px] text-stone-500" data-testid="criteria-grounding-note">
                <span className="font-medium text-stone-700">Grounding · Not required:</span> {grounding.notReq}
              </p>

              <div className="mt-4 rounded-md border border-stone-200 bg-white p-4 text-[13px] text-stone-600" data-testid="criteria-psd-note">
                <p><span className="font-medium text-stone-800">Overall Structure (PSD):</span> Not its own rubric. It is the average of the applicable dimension scores (0–4); Grounding is excluded when it is Not required. Scores may move up or down across pieces and revisions — that variation is expected and is not automatically improvement or decline.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
